import { Col, DatePicker, InputNumber, Row, Tag, Typography } from 'antd'
import { Button } from 'common/components/Button/Button'
import { Label } from 'common/components/Label/Label'
import { Modal } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useLazyFindAvailableSlotsQuery } from 'features/Schedule/api/schedule.api'
import { type AvailableSlot } from 'features/Schedule/api/schedule.types'
import { FormikProvider, useFormik } from 'formik'
import { type FC, useEffect } from 'react'
import { useParams } from 'react-router'
import * as Yup from 'yup'
import styles from './findAvailableModal.module.scss'

dayjs.extend(customParseFormat)

const { Text } = Typography

type FormValues = {
  searchDate: Dayjs | null
  numberOfDays: number
}

type Props = {
  isOpen: boolean
  onClose: () => void
  onSelectTime: (params: { startTime: Dayjs; endTime: Dayjs }) => void
  defaultDate: string
  roomType: number
  duration: number
  isDateDisabled: (date: Dayjs) => boolean
}

const validationSchema = Yup.object({
  searchDate: Yup.mixed().nullable().required('Start date is required'),
  numberOfDays: Yup.number()
    .min(1, 'Number of days must be at least 1')
    .max(30, 'Number of days cannot exceed 30')
    .required('Number of days is required'),
})

export const FindAvailableModal: FC<Props> = ({
  isOpen,
  onClose,
  onSelectTime,
  defaultDate,
  roomType,
  duration,
  isDateDisabled,
}) => {
  const { siteId } = useParams()

  const [findAvailableSlots, { data, isFetching, isSuccess }] = useLazyFindAvailableSlotsQuery()

  const formik = useFormik<FormValues>({
    initialValues: {
      searchDate: dayjs(defaultDate, 'YYYY-MM-DD'),
      numberOfDays: 7,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleSearch,
  })

  const searchResults = data
    ? Object.entries(data).map(([date, dayData]) => ({
        date,
        available_times: dayData.slots,
      }))
    : []

  const { values, errors, touched, handleSubmit, setFieldValue } = formik

  async function handleSearch(values: FormValues) {
    findAvailableSlots({
      siteId: siteId,
      date: values.searchDate?.format('YYYY-MM-DD'),
      for_days: values.numberOfDays,
      duration,
      room_type: roomType,
    })
  }

  const handleTimeSelect = (availableTime: AvailableSlot) => {
    const startTime = dayjs(availableTime.start)
    const endTime = dayjs(availableTime.end)
    onSelectTime({ startTime, endTime })
    onClose()
  }

  const formatDisplayDate = (date: string) => {
    return dayjs(date).format('dddd, MMMM D, YYYY')
  }

  // Fetch initial data when modal opens
  useEffect(() => {
    if (isOpen && defaultDate && roomType) {
      handleSearch(values)
    }
  }, [isOpen, roomType, defaultDate])

  return (
    <Modal
      classNames={{
        content: styles.content,
      }}
      destroyOnHidden
      title='Find Available Appointments'
      open={isOpen}
      onCancel={onClose}
      width={900}
      footer={null}>
      <FormikProvider value={formik}>
        <form className={styles.searchContainer} id='find-available-form' onSubmit={handleSubmit}>
          <Row gutter={[16, 16]} align='middle'>
            <Col xs={24} sm={9}>
              <div>
                <Label label='Start Date' />
                <DatePicker
                  maxDate={dayjs().add(6, 'month').endOf('month')}
                  minDate={dayjs()}
                  allowClear={false}
                  size='large'
                  value={values.searchDate}
                  onChange={(date) => setFieldValue('searchDate', date)}
                  format='MM-DD-YYYY'
                  style={{ width: '100%' }}
                  status={errors.searchDate && touched.searchDate ? 'error' : undefined}
                  disabledDate={isDateDisabled}
                />
                {errors.searchDate && touched.searchDate && (
                  <p className='errorMessage'>{String(errors.searchDate)}</p>
                )}
              </div>
            </Col>
            <Col xs={24} sm={9}>
              <div>
                <Label label='Number of Days' />
                <InputNumber
                  size='large'
                  value={values.numberOfDays}
                  onChange={(value) => setFieldValue('numberOfDays', value || 1)}
                  min={1}
                  max={30}
                  style={{ width: '100%' }}
                  status={errors.numberOfDays && touched.numberOfDays ? 'error' : undefined}
                />
                {errors.numberOfDays && touched.numberOfDays && (
                  <p className='errorMessage'>{errors.numberOfDays}</p>
                )}
              </div>
            </Col>
            <Col xs={24} sm={6}>
              <div className={styles.searchButtonContainer}>
                <Button
                  htmlType='submit'
                  form='find-available-form'
                  disabled={!values.searchDate || values.numberOfDays < 1 || isFetching}>
                  Search
                </Button>
              </div>
            </Col>
          </Row>
        </form>

        {isSuccess && searchResults.length === 0 && (
          <div className={styles.noResultsContainer}>
            <div className={styles.noResultsContent}>
              <Text strong className={styles.noResultsTitle}>
                No Available Appointments Found
              </Text>
              <Text className={styles.noResultsMessage}>
                We couldn't find any available appointments matching your criteria. Try:
              </Text>
              <ul className={styles.suggestionsList}>
                <li>Extending the search period (increase number of days)</li>
                <li>Choosing a different start date</li>
                <li>Selecting a shorter duration</li>
                <li>Checking back later for newly available slots</li>
              </ul>
            </div>
          </div>
        )}

        {searchResults.length > 0 && (
          <div className={styles.resultsContainer}>
            <div className={styles.daysContainer}>
              {searchResults.map((day) => (
                <div key={day.date} className={styles.daySection}>
                  <Text strong className={styles.dayTitle}>
                    {formatDisplayDate(day.date)}
                  </Text>

                  {day.available_times.length === 0 ? (
                    <Text className={styles.noTimes}>No available times</Text>
                  ) : (
                    <div className={styles.timesContainer}>
                      {day.available_times.map((time) => (
                        <Tag
                          key={time.start}
                          className={styles.timeChip}
                          onClick={() => handleTimeSelect(time)}>
                          <span className={styles.timeRange}>
                            {dayjs(time.start).format('hh:mm A')}
                          </span>
                        </Tag>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {isFetching && (
          <div className={styles.loadingContainer}>
            <Text>Searching for available appointments...</Text>
          </div>
        )}
      </FormikProvider>
    </Modal>
  )
}
